import { Instagram, Mail, MessageCircle, Phone } from 'lucide-react';
import React from 'react';

const ContactUs = () => {
    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 py-12 md:py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="text-center mb-12 md:mb-16 animate-fade-in">
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
                        Get in Touch
                    </h1>
                    <p className="text-lg sm:text-xl text-gray-600 font-medium max-w-2xl mx-auto">
                        We're here to help you with your driving journey
                    </p>
                </div>

                {/* Contact Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16">
                    {/* Phone Card */}
                    <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 hover:border-indigo-200">
                        <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                            <Phone className="w-8 h-8 text-indigo-600" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">Call Us</h3>
                        <p className="text-gray-600 mb-6 text-center">Speak directly with our team</p>
                        <div className="space-y-3">
                            <a
                                href="tel:+18668666630"
                                className="block text-center text-indigo-600 font-semibold text-lg py-3 px-4 rounded-xl hover:bg-indigo-50 transition-all duration-200 hover:scale-105"
                            >
                                +1 (866) 866-6630
                            </a>
                            <a
                                href="tel:+17787585859"
                                className="block text-center text-indigo-600 font-semibold text-lg py-3 px-4 rounded-xl hover:bg-indigo-50 transition-all duration-200 hover:scale-105"
                            >
                                +1 (778) 758-5859
                            </a>
                        </div>
                    </div>

                    {/* WhatsApp Card */}
                    <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 hover:border-green-200">
                        <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                            <MessageCircle className="w-8 h-8 text-green-600" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">WhatsApp</h3>
                        <p className="text-gray-600 mb-6 text-center">Message us anytime for quick support</p>
                        <a
                            href="https://wa.me/17787585859"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-center bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 hover:scale-105 shadow-md hover:shadow-lg"
                        >
                            WhatsApp Us
                        </a>
                    </div>

                    {/* Email Card */}
                    <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 hover:border-blue-200">
                        <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                            <Mail className="w-8 h-8 text-blue-600" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">Email Support</h3>
                        <p className="text-gray-600 mb-6 text-center">Send us an email and we'll respond shortly</p>
                        <a
                            href="mailto:info@lima.ac?subject=Support%20request&body=Hi%20Support,%0A%0A"
                            className="block text-center bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 hover:scale-105 shadow-md hover:shadow-lg"
                        >
                            Contact Support
                        </a>
                    </div>
                </div>

                {/* Instagram Section */}
                <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl border border-gray-100 text-center transform hover:scale-[1.02] transition-all duration-300">
                    <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
                        Follow Us on Instagram
                    </h2>
                    <p className="text-gray-600 mb-8 text-lg max-w-2xl mx-auto">
                        Stay updated with our latest news, student success stories, and driving tips
                    </p>
                    <a
                        href="https://instagram.com/limadrivingschool"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 hover:from-purple-700 hover:via-pink-700 hover:to-red-700 text-white font-bold text-lg py-5 px-10 rounded-2xl transition-all duration-300 hover:scale-105 hover:shadow-2xl shadow-lg"
                    >
                        <Instagram className="w-6 h-6" />
                        @limadrivingschool
                    </a>
                </div>

                {/* Additional Info Section */}
                <div className="mt-12 text-center">
                    <p className="text-gray-500 text-sm md:text-base">
                        Our support team is available Monday - Friday, 9:00 AM - 6:00 PM PST
                    </p>
                </div>
            </div>

            {/* Custom CSS for animations */}
            <style jsx>{`
                @keyframes fade-in {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .animate-fade-in {
                    animation: fade-in 0.6s ease-out;
                }

                @media (prefers-reduced-motion: reduce) {
                    .animate-fade-in {
                        animation: none;
                    }
                }
            `}</style>
        </div>
    );
};

export default ContactUs;

